import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Check } from "lucide-react";
import type { CalendarPlatform } from "@/types/contentCalendar.type";
import { isDevMode } from "@/config/envvars";
import { cn } from "@/utils/helpers";
import { KitSegmentedControl } from "../../components/kit/SegmentedControl";
import { useLivePostHistories } from "../api/useScheduling";
import { compactNumber, type MetricKind } from "./liveModel";
import { PlatformMark } from "./LiveParts";

const KINDS: MetricKind[] = ["views", "likes", "comments", "shares", "saves"];
const WORD: Record<MetricKind, string> = { views: "Views", likes: "Likes", comments: "Comments", shares: "Shares", saves: "Saves" };
const HOUR = 3_600_000;
const DAY = 24 * HOUR;
/** The daily axis: the first week, longer only when reads ran past it (a late Refresh), never past a month. */
const WEEK_DAYS = 7;
const MAX_DAYS = 30;
/** How late a read may run (the cron's minute tick and its 5-minute lease) and still count inside the first day. */
const LATE = 15 * 60_000;

export type Granularity = "hourly" | "daily";

/**
 * Figma 08, 6 Live, "Plot · views, one line per channel": the top gridline 24
 * down, the hour labels in the last 30.
 */
const TOP = 24;
const FOOT = 30;

export interface ChartPlatform {
  platform: CalendarPlatform;
  /** The channel's name, as the line's end label and its chip write it. */
  label: string;
  metricsUpdatedAt: string | null | undefined;
  /** When the platform's post went live: Daily opens a day after the first one. */
  liveSince: string;
}

interface Point {
  at: number;
  value: number;
}

interface Series {
  key: string;
  platform: CalendarPlatform;
  label: string;
  kind: MetricKind;
  points: Point[];
}

/**
 * A line's look by its place (Figma 08): the leader in ink, the next in grey,
 * the third dashed, the rest dotted. The place is taken over every line of
 * the number, shown or not, so hiding one never restyles the others.
 */
const LINE_STYLES = [
  { color: "var(--co-text)", dash: undefined },
  { color: "var(--co-muted)", dash: undefined },
  { color: "var(--co-muted)", dash: "4 4" },
  { color: "var(--co-muted)", dash: "0.5 4" },
] as const;
const styleAt = (rank: number) => LINE_STYLES[Math.min(rank, LINE_STYLES.length - 1)];

/**
 * Dev only: a week of reads as the cron takes them (hourly for a day, then
 * daily to day 7), ending now, so the graph can be judged on real-looking
 * data before enough has been collected. Never sent anywhere.
 */
const MOCK_SCALE = [12_400, 5_500, 1_600, 900];
const MOCK_SHARE: Record<MetricKind, number> = { views: 1, likes: 0.079, comments: 0.0033, shares: 0.0097, saves: 0.025 };

function mockHistory(index: number, start: number): { at: number; values: Record<MetricKind, number> }[] {
  const total = MOCK_SCALE[index % MOCK_SCALE.length] * 2.2;
  const hours = [...Array.from({ length: 25 }, (_, hour) => hour), 48, 72, 96, 120, 144, 168];
  const seen: Record<MetricKind, number> = { views: 0, likes: 0, comments: 0, shares: 0, saves: 0 };
  return hours.map((hour) => {
    // Fast at first, flattening over the week, with a little wobble per platform.
    const curve = (1 - Math.exp(-hour / (14 + index * 6))) / (1 - Math.exp(-168 / (14 + index * 6)));
    const wobble = 1 + 0.04 * Math.sin(hour * 1.7 + index);
    const values = {} as Record<MetricKind, number>;
    for (const kind of KINDS) {
      seen[kind] = Math.max(seen[kind], Math.round(total * MOCK_SHARE[kind] * curve * wobble));
      values[kind] = seen[kind];
    }
    return { at: start + hour * HOUR, values };
  });
}

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

/** Clock parts of a moment in the brand's time zone. */
function useZoned(timeZone: string) {
  return useMemo(() => {
    const format = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      weekday: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    const parts = (time: number) => {
      const map = Object.fromEntries(format.formatToParts(time).map((part) => [part.type, part.value]));
      return {
        weekday: map.weekday as string,
        day: Number(map.day),
        hour: Number(map.hour),
        minute: Number(map.minute),
        second: Number(map.second),
      };
    };
    const offsetInHour = (time: number) => {
      const p = parts(time);
      return p.minute * 60_000 + p.second * 1000 + (time % 1000);
    };
    return {
      parts,
      /** The start of the hour the moment falls in, on the brand's clock. */
      hourStart: (time: number) => time - offsetInHour(time),
      /** The brand's midnight before the moment. */
      dayStart: (time: number) => time - parts(time).hour * HOUR - offsetInHour(time),
      clock: (time: number) => {
        const p = parts(time);
        return `${String(p.hour).padStart(2, "0")}:${String(p.minute).padStart(2, "0")}`;
      },
    };
  }, [timeZone]);
}

/** Round steps for the gridlines: 1, 2, 2.5 or 5 times a power of ten, at most four of them to the top. */
function gridTicks(max: number): number[] {
  const top = Math.max(max, 1);
  const power = 10 ** Math.floor(Math.log10(top));
  const step = [0.1, 0.2, 0.25, 0.5, 1, 2, 2.5, 5, 10].map((n) => n * power).find((n) => top / n <= 4) ?? 10 * power;
  const count = Math.max(1, Math.ceil(top / step - 1e-9));
  return Array.from({ length: count + 1 }, (_, index) => index * step);
}

/** A line's latest value. */
const last = (entry: Series) => entry.points[entry.points.length - 1]?.value ?? 0;

/** Channel chip (library "Channel chip", Figma 08): a capsule with the logo, the name and a check while it is on. */
function Chip({ on, onPress, glyph, label }: { on: boolean; onPress: () => void; glyph?: ReactNode; label: string }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onPress}
      className={cn(
        "inline-flex h-10 items-center gap-2 rounded-full border border-co-line2 px-3 text-[13px] font-medium leading-[17.8px] outline-none transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent",
        on ? "bg-[var(--co-tonal)] text-co-text" : "text-co-muted hover:text-co-text",
      )}
    >
      {glyph}
      {label}
      {on ? <Check className="size-3.5" strokeWidth={2} aria-hidden="true" /> : null}
    </button>
  );
}

/**
 * The live posts' numbers over time, as one graph (Figma 08, 6 Live, "Graph ·
 * views by channel"): channel chips to hide a line and Hourly or Daily above
 * it; one line per channel for the number picked outside (the five numbers),
 * each labelled where it ends, with NOW at the latest read. Hourly is the
 * first 24 hours from the hour of the first read; Daily is the first week, a
 * point a day (opens once a day has passed). One platform's dialog
 * (`perPlatform`) instead draws several of its numbers together, a line each.
 */
export default function LiveNumbersChart({
  slotId,
  platforms,
  timeZone,
  kind: pickedKind = "views",
  granularity: controlled,
  onGranularity,
  perPlatform = false,
  height = 254,
}: {
  slotId: string;
  platforms: ChartPlatform[];
  timeZone: string;
  /** The number the lines draw, picked in the five numbers above (not in the dialog). */
  kind?: MetricKind;
  granularity?: Granularity;
  onGranularity?: (value: Granularity) => void;
  /** One platform's dialog: any of its numbers together, a line each. */
  perPlatform?: boolean;
  height?: number;
}) {
  const queries = useLivePostHistories(
    slotId,
    platforms.map((entry) => ({ platform: entry.platform, metricsUpdatedAt: entry.metricsUpdatedAt })),
  );
  const [hiddenPlatforms, setHiddenPlatforms] = useState<Set<CalendarPlatform>>(() => new Set());
  const [hiddenKinds, setHiddenKinds] = useState<Set<MetricKind>>(() => new Set());
  const [own, setOwn] = useState<Granularity>("hourly");
  const [mock, setMock] = useState(false);
  // The mock week ends on the latest whole hour.
  const mockStart = useMemo(() => Math.floor(Date.now() / HOUR) * HOUR - 168 * HOUR + 2 * 60_000, []);

  const loading = !mock && queries.some((query) => query.isLoading);
  const dataKey = queries.map((query) => query.dataUpdatedAt).join(",");

  // Every number of every platform, as the points that have it.
  const all = useMemo(() => {
    const list: Series[] = [];
    platforms.forEach((entry, index) => {
      const history = mock
        ? mockHistory(index, mockStart).map((point) => ({ at: point.at, read: (kind: MetricKind) => point.values[kind] as number | null }))
        : (queries[index]?.data ?? []).map((point) => ({
            at: new Date(point.readAt).getTime(),
            read: (kind: MetricKind) => (typeof point[kind] === "number" ? (point[kind] as number) : null),
          }));
      for (const kind of KINDS) {
        const points = history.flatMap((point) => {
          const value = point.read(kind);
          return value === null ? [] : [{ at: point.at, value }];
        });
        if (points.length) list.push({ key: `${entry.platform}:${kind}`, platform: entry.platform, label: entry.label, kind, points });
      }
    });
    return list;
    // queries is a fresh array each render; dataKey says when its data changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platforms, dataKey, mock, mockStart]);

  const kinds = KINDS.filter((entry) => all.some((series) => series.kind === entry));
  const withData = platforms.filter((entry) => all.some((series) => series.platform === entry.platform));
  const kind: MetricKind = kinds.includes(pickedKind) ? pickedKind : (kinds[0] ?? "views");
  // The lines this view could draw, shown or hidden: their places set their look.
  const candidates = perPlatform ? all : all.filter((series) => series.kind === kind);
  const ranked = [...candidates].sort((a, b) => last(b) - last(a));
  const rank = new Map(ranked.map((series, index) => [series.key, index]));
  const shown = candidates.filter((series) =>
    perPlatform ? !hiddenKinds.has(series.kind) : !hiddenPlatforms.has(series.platform),
  );

  // Daily opens once a full day has passed since the first channel went live.
  const firstLive = mock ? mockStart : Math.min(...withData.map((entry) => new Date(entry.liveSince).getTime()));
  const dailyOpen = Number.isFinite(firstLive) && Date.now() - firstLive >= DAY;
  const wanted = controlled ?? own;
  const granularity: Granularity = dailyOpen ? wanted : "hourly";
  const setGranularity = (value: Granularity) => (onGranularity ? onGranularity(value) : setOwn(value));

  const toggle = <T,>(set: Set<T>, value: T) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  };

  if (loading) {
    return (
      <div role="status" aria-label="Loading the numbers over time" className="flex flex-col gap-4">
        <div className="h-10 w-[min(100%,449px)] animate-pulse rounded-full bg-co-surface motion-reduce:animate-none" />
        <div className="animate-pulse rounded-card bg-co-surface motion-reduce:animate-none" style={{ height }} />
      </div>
    );
  }
  // Dev and local only: preview the graph on a mock week of reads.
  const mockToggle = isDevMode ? (
    <button
      type="button"
      aria-pressed={mock}
      onClick={() => setMock((on) => !on)}
      title="Dev only: a mock week of reads, hourly for a day then daily. Nothing is saved."
      className={cn(
        "inline-flex h-8 items-center gap-2 rounded-full border border-dashed px-3 text-[12.5px] outline-none transition-colors focus-visible:outline-2 focus-visible:outline-co-accent",
        mock ? "border-co-accent-border bg-[var(--co-accent-dim)] font-semibold text-co-text" : "border-co-line2 text-co-muted hover:text-co-text",
      )}
    >
      Mock data
    </button>
  ) : null;

  if (!all.length) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-[13px] text-co-muted">The graph fills in from the first read of the numbers.</p>
        {mockToggle}
      </div>
    );
  }

  const chips = perPlatform ? (
    kinds.length > 1 ? (
      <div role="group" aria-label="Numbers" className="flex flex-wrap gap-2">
        {kinds.map((entry) => (
          <Chip
            key={entry}
            on={!hiddenKinds.has(entry)}
            onPress={() => setHiddenKinds((previous) => toggle(previous, entry))}
            label={WORD[entry]}
          />
        ))}
      </div>
    ) : null
  ) : withData.length > 1 ? (
    <div role="group" aria-label="Channels" className="flex flex-wrap gap-2">
      {withData.map((entry) => (
        <Chip
          key={entry.platform}
          on={!hiddenPlatforms.has(entry.platform)}
          onPress={() => setHiddenPlatforms((previous) => toggle(previous, entry.platform))}
          glyph={<PlatformMark platform={entry.platform} className="size-3.5" />}
          label={entry.label}
        />
      ))}
    </div>
  ) : null;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        {chips}
        <div className="ml-auto flex items-center gap-2">
          {mockToggle}
          <KitSegmentedControl
            variant="lens"
            ariaLabel="Steps"
            className="h-10"
            value={granularity}
            onChange={setGranularity}
            options={[
              { label: "Hourly", value: "hourly" },
              {
                label: "Daily",
                value: "daily",
                disabled: !dailyOpen,
                title: dailyOpen ? undefined : "Daily opens a day after the post went live",
              },
            ]}
          />
        </div>
      </div>

      {shown.length ? (
        <Graph
          series={shown}
          rank={rank}
          timeZone={timeZone}
          byNumber={perPlatform}
          granularity={granularity}
          height={height}
        />
      ) : (
        <p className="text-[13px] text-co-muted" style={{ minHeight: height }}>
          {perPlatform ? "Turn on a number to see the graph." : "Turn on a channel to see the graph."}
        </p>
      )}
    </div>
  );
}

/** Mono axis words (JetBrains Mono 11, +0.12em). */
const MONO = "font-mono text-[11px] leading-[14.5px] tracking-[0.12em] whitespace-nowrap";

/** The plot: gridlines, a line per channel with its end label, NOW at the latest read, a crosshair on hover. */
function Graph({
  series: raw,
  rank,
  timeZone,
  byNumber,
  granularity,
  height,
}: {
  series: Series[];
  rank: Map<string, number>;
  timeZone: string;
  /** Lines are named by their number (one platform's dialog), else by their channel. */
  byNumber: boolean;
  granularity: Granularity;
  height: number;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hoverAt, setHoverAt] = useState<number | null>(null);
  const zoned = useZoned(timeZone);
  const daily = granularity === "daily";
  const base = height - FOOT;

  // The reads. Hourly: the first 24 hours from the hour of the first read.
  // Daily: a point a day (its last read), from the first read's day.
  const firstRead = Math.min(...raw.map((entry) => entry.points[0].at));
  const start = daily ? zoned.dayStart(firstRead) : zoned.hourStart(firstRead);
  const dayOf = (at: number) => Math.round((zoned.dayStart(at) - start) / DAY);
  const latestRead = Math.max(...raw.flatMap((entry) => entry.points.map((point) => point.at)));

  const series = raw
    .map((entry) => {
      if (!daily) return { ...entry, points: entry.points.filter((point) => point.at <= start + DAY + LATE) };
      const byDay = new Map<number, Point>();
      for (const point of entry.points) {
        const day = dayOf(point.at);
        if (day < MAX_DAYS) byDay.set(day, point);
      }
      return { ...entry, points: [...byDay.values()] };
    })
    .filter((entry) => entry.points.length > 0);

  const name = (entry: Series) => (byNumber ? WORD[entry.kind] : entry.label);
  const fullWidth = Math.max(width, 1);
  const labelWidth = (entry: Series) => 11 + name(entry).length * 7.4 + 8 + compactNumber(last(entry)).length * 11 + 8;
  // The axis always runs on past the last read, so the end labels have room
  // (Figma 08: NOW at 08:02, the hours go on to 18:00): the last point sits at
  // most four fifths across, less when the widest label needs more (a phone,
  // up to 45% room). Hourly shows at least the first day, in six-hour steps;
  // Daily at least the week.
  const room = Math.min(0.45, Math.max(0.2, ...series.map((entry) => labelWidth(entry) / fullWidth)));
  const lastAt = Math.max(start, ...series.map((entry) => entry.points[entry.points.length - 1].at));
  const end = start + Math.max(DAY, Math.ceil((lastAt - start) / (1 - room) / (6 * HOUR)) * 6 * HOUR);
  const days = Math.max(WEEK_DAYS, Math.ceil((dayOf(lastAt) + 0.5) / (1 - room)));
  const x = (at: number) =>
    daily ? ((dayOf(at) + 0.5) / days) * fullWidth : Math.min(Math.max((at - start) / (end - start), 0), 1) * fullWidth;
  const ticks = gridTicks(Math.max(0, ...series.flatMap((entry) => entry.points.map((point) => point.value))));
  const top = ticks[ticks.length - 1] || 1;
  const y = (value: number) => base - (value / top) * (base - TOP);

  // NOW sits at the latest read, when the view reaches it (Hourly a week on
  // shows the first day only, with no NOW).
  const lastDrawn = series.length ? Math.max(...series.map((entry) => entry.points[entry.points.length - 1].at)) : null;
  const nowAt = lastDrawn === latestRead ? lastDrawn : null;
  const nowParts = nowAt === null ? null : zoned.parts(nowAt);
  const nowLabel = nowAt === null ? "" : `Now · ${daily ? `${nowParts!.weekday} ` : ""}${zoned.clock(nowAt)}`;
  const nowX = nowAt === null ? 0 : x(nowAt);
  const nowFlip = nowX + 8 + nowLabel.length * 8 > fullWidth;

  // The x axis words: every six hours (twelve when they would touch), or each
  // day (every other one when they would touch).
  const spanHours = Math.round((end - start) / HOUR);
  const hourStep = fullWidth / (spanHours / 6) >= 60 ? 6 : 12;
  const axis = daily
    ? Array.from({ length: days }, (_, day) => day)
        .filter((day) => fullWidth / days >= 58 || day % 2 === 0)
        .map((day) => ({ key: day, left: ((day + 0.5) / days) * fullWidth, align: "center" as const, text: `${zoned.parts(start + day * DAY + 12 * HOUR).weekday} ${zoned.parts(start + day * DAY + 12 * HOUR).day}` }))
    : Array.from({ length: Math.floor(spanHours / hourStep) + 1 }, (_, index) => index * hourStep).map((hour) => ({
        key: hour,
        left: (hour / spanHours) * fullWidth,
        align: hour === 0 ? ("start" as const) : hour === spanHours ? ("end" as const) : ("center" as const),
        text: zoned.clock(start + hour * HOUR),
      }));

  // End labels: beside each line's last point, pushed apart when two would
  // touch, and to the left of the point only if the room still runs out.
  const labels = (() => {
    const list = series.map((entry) => {
      const point = entry.points[entry.points.length - 1];
      const text = compactNumber(point.value);
      const px = x(point.at);
      const flip = px + labelWidth(entry) > fullWidth;
      return { entry, text, px, py: y(point.value), flip, top: y(point.value) - 11 };
    });
    const sorted = [...list].sort((a, b) => a.top - b.top);
    for (let index = 1; index < sorted.length; index++) {
      sorted[index].top = Math.max(sorted[index].top, sorted[index - 1].top + 24);
    }
    const overflow = sorted.length ? sorted[sorted.length - 1].top + 22 - base : 0;
    if (overflow > 0) for (const label of sorted) label.top -= overflow;
    return sorted;
  })();

  // Hover: the nearest time any line has a read at, and each line's value then.
  const times = [...new Set(series.flatMap((entry) => entry.points.map((point) => (daily ? dayOf(point.at) : point.at))))].sort((a, b) => a - b);
  const xOfTime = (time: number) => (daily ? ((time + 0.5) / days) * fullWidth : x(time));
  const valueAt = (entry: Series, time: number) => {
    let found: Point | null = null;
    for (const point of entry.points) {
      if ((daily ? dayOf(point.at) : point.at) <= time) found = point;
      else break;
    }
    return found;
  };
  const hoverLabel = (time: number) => {
    if (daily) {
      const p = zoned.parts(start + time * DAY + 12 * HOUR);
      return `${p.weekday} ${p.day}`;
    }
    const p = zoned.parts(time);
    return `${p.weekday} ${p.day}, ${zoned.clock(time)}`;
  };
  const summary = series.map((entry) => `${name(entry)} ${compactNumber(last(entry))}`).join(", ");

  return (
    <div ref={ref} className="relative w-full" style={{ height }}>
      {width > 0 ? (
        <>
          <svg
            width={width}
            height={height}
            role="img"
            aria-label={`The numbers over time. At the last read: ${summary}.`}
            onPointerMove={(event) => {
              const px = event.clientX - event.currentTarget.getBoundingClientRect().left;
              let nearest = times[0];
              for (const time of times) if (Math.abs(xOfTime(time) - px) < Math.abs(xOfTime(nearest) - px)) nearest = time;
              setHoverAt(nearest ?? null);
            }}
            onPointerLeave={() => setHoverAt(null)}
            className="absolute inset-0 block touch-none overflow-visible"
          >
            {ticks.map((value) => (
              <line key={value} x1={0} x2={width} y1={y(value)} y2={y(value)} stroke="var(--co-line)" strokeWidth={1} shapeRendering="crispEdges" />
            ))}
            {nowAt !== null ? (
              <line x1={nowX} x2={nowX} y1={8} y2={base} stroke="var(--co-muted)" strokeWidth={1} strokeDasharray="2 4" />
            ) : null}
            {hoverAt !== null ? (
              <line x1={xOfTime(hoverAt)} x2={xOfTime(hoverAt)} y1={TOP} y2={base} stroke="var(--co-line2)" strokeWidth={1} />
            ) : null}
            {series.map((entry) => {
              const style = styleAt(rank.get(entry.key) ?? 0);
              const path = entry.points.map((point, index) => `${index ? "L" : "M"}${x(point.at).toFixed(1)},${y(point.value).toFixed(1)}`).join(" ");
              const end = entry.points[entry.points.length - 1];
              return (
                <g key={entry.key}>
                  {entry.points.length > 1 ? (
                    <path d={path} fill="none" stroke={style.color} strokeWidth={2} strokeDasharray={style.dash} strokeLinejoin="round" strokeLinecap="round" />
                  ) : null}
                  {daily
                    ? entry.points.slice(0, -1).map((point) => <circle key={point.at} cx={x(point.at)} cy={y(point.value)} r={3} fill={style.color} />)
                    : null}
                  <circle cx={x(end.at)} cy={y(end.value)} r={3.5} fill={style.color} />
                </g>
              );
            })}
            {hoverAt !== null
              ? series.map((entry) => {
                  const point = valueAt(entry, hoverAt);
                  return point ? (
                    <circle key={entry.key} cx={x(point.at)} cy={y(point.value)} r={4.5} fill={styleAt(rank.get(entry.key) ?? 0).color} stroke="var(--co-ground)" strokeWidth={2} />
                  ) : null;
                })
              : null}
          </svg>

          {/* The words, as HTML over the plot so they set in the page's own faces. */}
          {ticks.slice(1).map((value) => (
            <span key={value} aria-hidden="true" className={cn(MONO, "pointer-events-none absolute left-0 text-co-muted")} style={{ top: y(value) - 17 }}>
              {compactNumber(value)}
            </span>
          ))}
          {nowAt !== null ? (
            <span
              aria-hidden="true"
              className={cn(MONO, "pointer-events-none absolute top-0.5 uppercase text-co-text2")}
              style={nowFlip ? { right: fullWidth - nowX + 8 } : { left: nowX + 8 }}
            >
              {nowLabel}
            </span>
          ) : null}
          {labels.map((label) => {
            const style = styleAt(rank.get(label.entry.key) ?? 0);
            return (
              <span
                key={label.entry.key}
                aria-hidden="true"
                className="pointer-events-none absolute flex h-[22px] items-center gap-2 whitespace-nowrap"
                style={{ top: label.top, color: style.color, ...(label.flip ? { right: fullWidth - label.px + 11 } : { left: label.px + 11 }) }}
              >
                <span className="text-[13px] font-semibold leading-[17.8px]">{name(label.entry)}</span>
                <span className="font-section text-[22px] font-semibold leading-[22px]">{label.text}</span>
              </span>
            );
          })}
          {axis.map((tick) => (
            <span
              key={tick.key}
              aria-hidden="true"
              className={cn(MONO, "pointer-events-none absolute text-co-muted")}
              style={{
                top: base + 10,
                left: tick.align === "end" ? undefined : tick.left,
                right: tick.align === "end" ? 0 : undefined,
                transform: tick.align === "center" ? "translateX(-50%)" : undefined,
              }}
            >
              {tick.text}
            </span>
          ))}

          {hoverAt !== null ? (
            <div
              className="co-glass-chrome pointer-events-none absolute z-[2] flex min-w-[160px] flex-col gap-1 rounded-card px-3 py-2 text-[12px] text-co-text"
              style={{
                top: TOP + 4,
                // Beside the crosshair, on whichever side has the room.
                ...(xOfTime(hoverAt) + 12 + 176 > width ? { right: width - xOfTime(hoverAt) + 12 } : { left: xOfTime(hoverAt) + 12 }),
              }}
            >
              <span className="text-co-muted">{hoverLabel(hoverAt)}</span>
              {series.map((entry) => {
                const point = valueAt(entry, hoverAt);
                return point ? (
                  <span key={entry.key} className="flex items-center justify-between gap-4">
                    <span>{name(entry)}</span>
                    <span className="font-semibold tabular-nums">{compactNumber(point.value)}</span>
                  </span>
                ) : null;
              })}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
